import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { estimatePackageShipping, type Product, type ShippingDimensions } from "@nh/shared";
import { useProductPicker } from "./packages.queries";
import {
  computePackagePreview,
  indexProducts,
  type PackagePreview,
} from "./packagePricing";
import { readDiscountPercent, readPackageItems, type PackageFormValues } from "./packageForm";

export interface PackagePreviewState {
  preview: PackagePreview;
  /** El catálogo con el que se hizo la cuenta. */
  products: Product[];
  catalog: ReadonlyMap<string, Product>;
  loading: boolean;
  error: Error | null;
  /**
   * `true` si el catálogo tiene más productos de los que caben en una
   * página del selector. Mientras lo sea, faltan productos por elegir y
   * el precio de un paquete que use uno de ellos saldría incompleto.
   */
  truncated: boolean;
  /** El descuento entendido, o `null` mientras se está escribiendo. */
  discountPercent: number | null;
  /**
   * La caja que SALE de sumar los productos elegidos (sin margen): lo que
   * viaja a Trasso en cada campo que el paquete no declare. La sección de
   * envío la enseña como propuesta.
   */
  shippingSum: ShippingDimensions;
}

/**
 * El precio del paquete, recalculado en cada tecla.
 *
 * ── Por qué `useWatch` y no `watch` ──────────────────────────────────
 * `useWatch` suscribe SOLO a los campos nombrados: escribir en la
 * descripción no vuelve a sumar precios. Con `watch()` a secas, cada
 * letra de cualquier campo del formulario rehace la cuenta entera.
 *
 * ── Por qué el catálogo entra aquí y no en el componente ────────────
 * Porque la cuenta necesita las dos mitades a la vez —qué productos y a
 * qué precio— y repartirlas deja al componente juntándolas a mano. Aquí
 * salen ya juntas y el componente solo pinta.
 */
export function usePackagePreview(): PackagePreviewState {
  const { control } = useFormContext<PackageFormValues>();
  const catalogQuery = useProductPicker();

  const items = useWatch({ control, name: "items" });
  const rawDiscount = useWatch({ control, name: "discountPercent" });

  const products = useMemo(() => catalogQuery.data?.items ?? [], [catalogQuery.data]);
  const catalog = useMemo(() => indexProducts(products), [products]);

  const discountPercent = readDiscountPercent(rawDiscount ?? "");

  const preview = useMemo(
    () =>
      computePackagePreview(
        readPackageItems(items ?? []),
        catalog,
        /*
         * Mientras el descuento no se entiende —el campo vacío, o una
         * letra— la cuenta se hace con 0 %: el precio de lista sigue
         * siendo cierto y se enseña, en vez de dejar el bloque entero en
         * blanco a media escritura. La pantalla dice que no hay
         * descuento aplicado todavía.
         */
        discountPercent ?? 0,
      ),
    [items, catalog, discountPercent],
  );

  const shippingSum = useMemo(
    () =>
      estimatePackageShipping(
        readPackageItems(items ?? []).flatMap((item) => {
          const product = catalog.get(item.productId);
          return product ? [{ product, quantity: item.quantity }] : [];
        }),
      ).exact,
    [items, catalog],
  );

  return {
    preview,
    products,
    catalog,
    shippingSum,
    loading: catalogQuery.isPending,
    error: catalogQuery.error,
    truncated: (catalogQuery.data?.total ?? 0) > products.length,
    discountPercent,
  };
}
